import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Stack,
  Text,
  Button,
  Flex,
  Box,
  Card,
  HStack,
  Icon,
  IconButton,
  Table,
} from "@chakra-ui/react";
import { Link, useParams } from "react-router-dom";
import {
  AppHeader,
  AppHeaderHeight,
  AppHeaderLogo,
} from "../../../shared/views/AppHeader/AppHeader";
import { useOsmToolsMap } from "./useOsmToolsMap";
import { useStores } from "../../../shared/stores/RootStore";
import { ArrowRightIcon, CloseIcon } from "../../../shared/views/icons";
import { appRoutes } from "../../../../App";
import { StreetViewComponent } from "../../../hla/views/StreetView/StreetViewComponent";
import { TranslateTagDialog } from "./TranslateTagDialog";

const AdminOsmToolsPageBase = () => {
  const { osmToolsStore } = useStores();
  const { cityId } = useParams();
  const [translateTag, setTranslateTag] = useState<{
    key: string;
    value: string;
  }>();

  const { city, data, polygon, selectedWay, allTags, highlightedTag } =
    osmToolsStore;

  useOsmToolsMap();

  useEffect(() => {
    osmToolsStore.resetState();
    osmToolsStore.fetchCity(cityId!);
    osmToolsStore.fetchData(cityId!);
    // eslint-disable-next-line
  }, []);

  const polygons = polygon
    ? polygon.geometry.type === "Polygon"
      ? [polygon.geometry.coordinates[0]]
      : polygon.geometry.coordinates.flat()
    : [];

  return (
    <Flex flexDir="column" maxHeight="100vh" flexGrow={1}>
      <AppHeader>
        <HStack
          gap={4}
          separator={<Icon as={ArrowRightIcon} borderInline={"none"} />}
          flexGrow={1}
        >
          <AppHeaderLogo />

          <Link to={appRoutes.adminCities()}>
            <Text fontSize="sm" fontWeight="semibold">
              Cities
            </Text>
          </Link>

          <Text fontSize="sm" fontWeight="semibold">
            {city?.name} ({city?.country})
          </Text>
        </HStack>
      </AppHeader>
      <AppHeaderHeight />

      <Flex flexGrow={1} maxW="container.xl" overflow="hidden">
        <Stack w={"400px"} overflow="auto" alignItems={"stretch"} p={3}>
          <Button
            size={"sm"}
            colorPalette={"primary"}
            disabled={!city || !!polygon}
            onClick={() => osmToolsStore.searchPolygon()}
          >
            Fetch city polygon
          </Button>

          {polygons.map((p, index) => (
            <Button
              size={"sm"}
              colorPalette={"primary"}
              loading={osmToolsStore.querying}
              onClick={() => {
                osmToolsStore.queryPolygon(cityId!, p);
              }}
            >
              Query OSM with polygon #{index}
            </Button>
          ))}

          {!!data?.ways.length && (
            <Button
              size={"sm"}
              colorPalette={"primary"}
              disabled={!city}
              onClick={() => osmToolsStore.deleteData(cityId!)}
            >
              Clear data
            </Button>
          )}

          {!!data && (
            <Stack pt={4}>
              <Text>Saved data</Text>
              <Text fontSize={"sm"}>
                {data?.ways?.length} ways • {data?.nodes?.length} nodes
              </Text>
            </Stack>
          )}

          {!!allTags && (
            <Table.Root size="sm" maxWidth={"100%"}>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Key</Table.ColumnHeader>
                  <Table.ColumnHeader>Value</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {Object.entries(allTags)
                  .toSorted((a, b) => a[0].localeCompare(b[0]))
                  .map(([key, values]) => (
                    <Table.Row key={key}>
                      <Table.Cell maxWidth={"160px"}>{key}</Table.Cell>
                      <Table.Cell>
                        <Stack>
                          {values.map((value) => {
                            const selected =
                              highlightedTag?.key === key &&
                              highlightedTag?.value === value;
                            return (
                              <Text
                                key={value}
                                maxWidth={"200px"}
                                color={selected ? "#c320e0" : "black"}
                                fontWeight={selected ? "semibold" : "normal"}
                                _hover={{ textDecor: "underline" }}
                                cursor={"pointer"}
                                onClick={() => {
                                  if (selected) {
                                    setTranslateTag({ key, value });
                                  } else {
                                    osmToolsStore.highlightTag(key, value);
                                  }
                                }}
                              >
                                {value}
                              </Text>
                            );
                          })}
                        </Stack>
                      </Table.Cell>
                    </Table.Row>
                  ))}
              </Table.Body>
            </Table.Root>
          )}
        </Stack>
        <Stack flexGrow={1} position={"relative"}>
          <Box id="map" flexGrow={1} height="100%" />

          <Box position={"absolute"} bottom={3} right={3}>
            {!!selectedWay && (
              <Card.Root w={"400px"} overflow={"hidden"}>
                <Stack p={3}>
                  <HStack>
                    <Text flexGrow={1} fontSize={"sm"} fontWeight={"semibold"}>
                      Way #{selectedWay.id}
                    </Text>
                    <IconButton
                      size="sm"
                      variant={"ghost"}
                      onClick={() => osmToolsStore.selectWay(undefined)}
                    >
                      <CloseIcon />
                    </IconButton>
                  </HStack>

                  <Table.Root size={"sm"}>
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Key</Table.ColumnHeader>
                        <Table.ColumnHeader>Value</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>

                    <Table.Body>
                      {Object.entries(selectedWay.tags).map(([key, value]) => (
                        <Table.Row key={key}>
                          <Table.Cell>{key}</Table.Cell>
                          <Table.Cell>{value}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Stack>

                <Box h={"300px"}>
                  <StreetViewComponent
                    streetViewMapSource={osmToolsStore.streetViewMapSource}
                  />
                </Box>
              </Card.Root>
            )}
          </Box>
        </Stack>
      </Flex>

      {!!translateTag && (
        <TranslateTagDialog
          cityId={cityId!}
          tag={translateTag}
          onClose={() => setTranslateTag(undefined)}
        />
      )}
    </Flex>
  );
};

export const AdminOsmToolsPage = observer(AdminOsmToolsPageBase);
